import React, { useState } from "react";
import styled from "styled-components";
import { Row, Col, Button, Table, Alert } from "components/bootstrap";
import { ConfirmDialog, Icon, NoSearchResult, Spinner } from "components/common";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useLocation from "routing/useLocation";
import { getPathnameWithoutId } from "util/URLUtils";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import RetrievalProvider from "data-lake/preview/storage-types/external/contexts/RetrievalProvider";
import EditConnectorModal from "./EditConnectorModal";
import useDataLakeConnectors, { deleteDataLakeConnector } from "./useDataLakeConnectors";
import DataLakeConnectorsActions from "./DataLakeConnectorActions";
import DataLakeConnectorStreamCell from "./DataLakeConnectorStreamCell";
import DataLakeConnectorCreateInputModal from "./DataLakeConnectorCreateInputModal";
const StyledButton = styled(Button)`
  float: right;
`;
const DataLakeConnectorsList = () => {
  const { data: connectors, refetch, isLoading } = useDataLakeConnectors();
  const [showConnectorModal, setShowConnectorModal] = useState(false);
  const [connectorIdToEdit, setConnectorIdToEdit] = useState(void 0);
  const [connectorToDelete, setConnectorToDelete] = useState(void 0);
  const [connectorIdForInputCreation, setConnectorIdForInputCreation] = useState(void 0);
  const sendTelemetry = useSendTelemetry();
  const { pathname } = useLocation();
  const handleEditConnector = (id) => {
    if (!id) {
      return;
    }
    setConnectorIdToEdit(id);
    setShowConnectorModal(true);
  };
  const handleCloseEditConnectorModal = () => {
    setConnectorIdToEdit(void 0);
    setShowConnectorModal(false);
  };
  const handleDeleteConnector = async () => {
    if (!(connectorToDelete == null ? void 0 : connectorToDelete.id)) {
      return;
    }
    await deleteDataLakeConnector(connectorToDelete.id);
    await refetch();
    setConnectorToDelete(void 0);
  };
  const handleAddConnectorClick = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.CONNECTORS.ADD_CONNECTOR_BUTTON_CLICKED, {
      app_pathname: getPathnameWithoutId(pathname)
    });
    setShowConnectorModal(true);
  };
  return /* @__PURE__ */ React.createElement(RetrievalProvider, null, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 6 }, /* @__PURE__ */ React.createElement("h2", null, "AWS Security Lake Connectors")), /* @__PURE__ */ React.createElement(Col, { md: 6 }, /* @__PURE__ */ React.createElement(StyledButton, { onClick: handleAddConnectorClick, bsStyle: "primary" }, /* @__PURE__ */ React.createElement(Icon, { name: "add" }), " Add Connector"))), /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, "This table contains Connectors to external Amazon Security Lakes. Note that each Connector is restricted to a single AWS region of a single AWS account; to cover multiple regions or accounts, multiple Connectors will be required."), showConnectorModal && /* @__PURE__ */ React.createElement(
    EditConnectorModal,
    {
      connectorId: connectorIdToEdit,
      onRefetch: refetch,
      onCancel: handleCloseEditConnectorModal
    }
  ), /* @__PURE__ */ React.createElement(Table, null, /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("th", null, "Name"), /* @__PURE__ */ React.createElement("th", null, "AWS Access Key"), /* @__PURE__ */ React.createElement("th", null, "AWS Region"), /* @__PURE__ */ React.createElement("th", null, "S3 Output Bucket"), /* @__PURE__ */ React.createElement("th", null, "Stream"), /* @__PURE__ */ React.createElement("th", { className: "text-right" }, "Actions"))), /* @__PURE__ */ React.createElement("tbody", null, connectors == null ? void 0 : connectors.map((connector) => /* @__PURE__ */ React.createElement("tr", { key: connector.id, "data-testid": `table-row-${connector.id}` }, /* @__PURE__ */ React.createElement("td", null, connector.data_lake_name), /* @__PURE__ */ React.createElement("td", null, connector.aws_access_key), /* @__PURE__ */ React.createElement("td", null, connector.aws_region), /* @__PURE__ */ React.createElement("td", null, connector.s3_output_bucket), /* @__PURE__ */ React.createElement(DataLakeConnectorStreamCell, { stream_id: connector.stream_id }), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(
    DataLakeConnectorsActions,
    {
      connector,
      onEdit: () => handleEditConnector(connector.id),
      onDelete: () => setConnectorToDelete(connector),
      onCreateInput: () => setConnectorIdForInputCreation(connector.id)
    }
  )))), !(connectors == null ? void 0 : connectors.length) && /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("td", { colSpan: 6 }, isLoading ? /* @__PURE__ */ React.createElement(Spinner, null) : /* @__PURE__ */ React.createElement(NoSearchResult, null, "No Data Lake Connectors have been found."))))), connectorToDelete && /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      title: "Delete Connector",
      show: true,
      onConfirm: handleDeleteConnector,
      onCancel: () => setConnectorToDelete(void 0)
    },
    /* @__PURE__ */ React.createElement("p", null, "Deleting this connector will also permanently remove all linked retrievals and the retrieval stream."),
    /* @__PURE__ */ React.createElement("p", null, "Are you sure you want to delete the data lake connector", " ", /* @__PURE__ */ React.createElement("b", null, /* @__PURE__ */ React.createElement("i", null, connectorToDelete == null ? void 0 : connectorToDelete.data_lake_name)), " ", "?")
  ), connectorIdForInputCreation && /* @__PURE__ */ React.createElement(
    DataLakeConnectorCreateInputModal,
    {
      connectorId: connectorIdForInputCreation,
      onClose: () => setConnectorIdForInputCreation(void 0)
    }
  ));
};
export default DataLakeConnectorsList;
